import { faClose, faCopy, faHouse, faPaperclip, faRulerCombined } from "@fortawesome/free-solid-svg-icons"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import GetSpace from "../../../utils/space"
import InputField from "../../../components/InputField/InputField"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useEffect } from "react"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { ERestaurantServiceType } from "../../../Enum/ERestaurantServiceType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface LinkPublicMenuPupopProps {
  onClonse: () => void
}

export const LinkPublicMenuPupop: React.FC<LinkPublicMenuPupopProps> = ({ onClonse }) => {
  const { t } = useTranslation("company")

  const { handleUpdateSlug } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const {
    slug,
    restaurantServiceType,
    localServiceRadiusMeters,
    handleSlugChange,
    handleRestaurantServiceTypeChange,
    handleLocalServiceRadiusMetersChange,
    handleInitialValue,
  } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
  }, [])

  const handleTrySave = () => {
    const parsedRadius = localServiceRadiusMeters.toNumber()

    const sanitizedRadius = parsedRadius > 0 ? parsedRadius : 100

    handleUpdateSlug(companyMemory.id, slug, restaurantServiceType, sanitizedRadius, onClonse)

    handleGetCompany(() => {})
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_public_menu")}
              iconColor={THEME.colors.iconBGColor}
              icon={faHouse}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.linkpublicmenupupop.1"
                    variant="global"
                    title={t("helpPopup:global.publicMenu.title")}
                    description={t("helpPopup:global.publicMenu.description")}
                    bullets={t("helpPopup:global.publicMenu.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.button.onClonse"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <GenericDropDown
                legend={t("label_service_type")}
                data={Object.mapEnumToStrings(ERestaurantServiceType)}
                initialState={restaurantServiceType}
                disable={canEdite}
                handlerFuncion={handleRestaurantServiceTypeChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.select.serviceType"
              />

              <GetSpace top={10} />

              <InputField
                maxLength={INPUT_SIZE.COMPANY.url}
                type="text"
                placeholder={t("label_link_menu_place_holder")}
                onInvalidMessage={t("label_link_menu_onInvalidMessage")}
                icon={faPaperclip}
                disable={canEdite}
                value={slug}
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(" ", "")
                }}
                onChange={handleSlugChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.input.linkMenuPlaceHolder"
              />

              {(restaurantServiceType === ERestaurantServiceType.Local ||
                restaurantServiceType === ERestaurantServiceType.Both) && (
                <>
                  <div className={defaultStyle.divisionBar}></div>

                  <InputField
                    type="number"
                    title={t("label_local_service_radius_title")}
                    placeholder={t("label_local_service_radius_placeholder")}
                    onInvalidMessage={t("label_local_service_radius_onInvalidMessage")}
                    icon={faRulerCombined}
                    disable={canEdite}
                    value={localServiceRadiusMeters}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/[^\d]/g, "")
                    }}
                    onChange={handleLocalServiceRadiusMetersChange}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.input.localServiceRadiusPlaceholder"
                  />

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <p className="mt-2 px-2 text-xs text-gray-500">{t("label_local_service_radius_hint")}</p>
                  </Animate>

                  <GetSpace top={10} />
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                {slug.length > 0 && (
                  <CanView permission={Permissions.CanEditeCompany}>
                    <Button
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.button.save"
                    />
                  </CanView>
                )}

                {slug.length > 0 && (
                  <>
                    <GetSpace left={5} />

                    <Button
                      size="small"
                      typeButton="button"
                      label={t("buttons:button_view_link")}
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      iconColor={THEME.colors.buttonFGSaveColor}
                      onClick={() => {
                        window.open(`${API_ROUTES.SITE}${slug}`, "_blank")
                      }}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.button.viewLink"
                    />

                    <GetSpace left={5} />

                    <Button
                      size="small"
                      typeButton="button"
                      label={t("buttons:button_copy")}
                      bgColor={THEME.colors.buttonBGPlusColor}
                      iconColor={THEME.colors.buttonFGSaveColor}
                      onClick={async () => {
                        await navigator.clipboard.writeText(`${API_ROUTES.SITE}${slug}`)
                      }}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.button.copy"
                    />
                  </>
                )}

                <GetSpace left={5} />

                <Button
                  size="small"
                  typeButton="button"
                  label={t("buttons:button_register")}
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  iconColor={THEME.colors.buttonFGSaveColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.linkPublicMenuPupop.button.register"
                />
              </div>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
